import { assign, setup, type ActorRefFrom, type SnapshotFrom } from "xstate";
import { newChargeAttemptId } from "./idempotency";
import {
  LitActorController,
  type LitActorHost,
} from "../state/lit-actor-controller";

export type CheckoutKind =
  | "class"
  | "event"
  | "program"
  | "appointment"
  | "product"
  | "gift";

export interface SubmissionResult {
  orderId?: string;
  invoiceIds?: readonly (string | undefined)[];
}

export interface SubmissionContext {
  kind: CheckoutKind;
  attemptId: string;
  paymentCommitted: boolean;
  childBusy: boolean;
  error: string | null;
  result: SubmissionResult | null;
}

export type SubmissionEvent =
  | { type: "checkout.kind.changed"; kind: CheckoutKind }
  | { type: "submit.requested" }
  | { type: "payment.required" }
  | { type: "payment.committed" }
  | { type: "submission.succeeded"; result?: SubmissionResult }
  | {
      type: "submission.failed";
      error: string;
      paymentCommitted?: boolean;
    }
  | { type: "child.busy.changed"; busy: boolean }
  | { type: "fulfillment.retry" }
  | { type: "submission.reset" };

export const submissionMachine = setup({
  types: {
    context: {} as SubmissionContext,
    events: {} as SubmissionEvent,
    input: {} as { kind: CheckoutKind },
  },
  guards: {
    paymentWasCommitted: ({ context }, params: { eventCommitted: boolean }) =>
      context.paymentCommitted || params.eventCommitted,
  },
  actions: {
    markRequested: assign({
      childBusy: false,
      error: null,
      result: null,
    }),
    markPaymentRequired: assign({
      childBusy: false,
      error: null,
    }),
    markPaymentCommitted: assign({
      paymentCommitted: true,
      childBusy: false,
      error: null,
    }),
    markSucceeded: assign({
      childBusy: false,
      error: null,
      result: (_, params: { result: SubmissionResult | undefined }) =>
        params.result ?? {},
    }),
    markFailure: assign({
      childBusy: false,
      error: (_, params: { error: string }) => params.error,
    }),
    prepareRetry: assign({
      attemptId: () => newChargeAttemptId(),
      paymentCommitted: false,
      childBusy: false,
      error: (_, params: { error: string }) => params.error,
      result: null,
    }),
    setChildBusy: assign({
      childBusy: (_, params: { busy: boolean }) => params.busy,
    }),
    reset: assign({
      attemptId: () => newChargeAttemptId(),
      paymentCommitted: false,
      childBusy: false,
      error: null,
      result: null,
    }),
    setKind: assign({
      kind: (_, params: { kind: CheckoutKind }) => params.kind,
    }),
  },
}).createMachine({
  id: "checkoutSubmission",
  context: ({ input }) => ({
    kind: input.kind,
    attemptId: newChargeAttemptId(),
    paymentCommitted: false,
    childBusy: false,
    error: null,
    result: null,
  }),
  on: {
    "checkout.kind.changed": {
      actions: {
        type: "setKind",
        params: ({ event }) => ({ kind: event.kind }),
      },
    },
    "child.busy.changed": {
      actions: {
        type: "setChildBusy",
        params: ({ event }) => ({ busy: event.busy }),
      },
    },
    "submission.reset": {
      target: ".editing",
      actions: "reset",
    },
  },
  initial: "editing",
  states: {
    editing: {
      on: {
        "submit.requested": {
          target: "submitting",
          actions: "markRequested",
        },
        // Wallet buttons can submit inside the Stripe child without a parent
        // CTA event. A committed child result is still irreversible and must
        // enter fulfillment atomically.
        "payment.committed": {
          target: "fulfilling",
          actions: "markPaymentCommitted",
        },
      },
    },
    submitting: {
      on: {
        "payment.required": {
          target: "awaitingPayment",
          actions: "markPaymentRequired",
        },
        "payment.committed": {
          target: "fulfilling",
          actions: "markPaymentCommitted",
        },
        "submission.succeeded": {
          target: "succeeded",
          actions: {
            type: "markSucceeded",
            params: ({ event }) => ({ result: event.result }),
          },
        },
        "submission.failed": [
          {
            guard: {
              type: "paymentWasCommitted",
              params: ({ event }) => ({
                eventCommitted: event.paymentCommitted ?? false,
              }),
            },
            target: "recoveryRequired",
            actions: [
              "markPaymentCommitted",
              {
                type: "markFailure",
                params: ({ event }) => ({ error: event.error }),
              },
            ],
          },
          {
            target: "editing",
            actions: {
              type: "prepareRetry",
              params: ({ event }) => ({ error: event.error }),
            },
          },
        ],
      },
    },
    awaitingPayment: {
      on: {
        "submit.requested": {
          target: "submitting",
          actions: "markRequested",
        },
        "payment.committed": {
          target: "fulfilling",
          actions: "markPaymentCommitted",
        },
        "submission.failed": {
          target: "editing",
          actions: {
            type: "prepareRetry",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    fulfilling: {
      on: {
        "submission.succeeded": {
          target: "succeeded",
          actions: {
            type: "markSucceeded",
            params: ({ event }) => ({ result: event.result }),
          },
        },
        "submission.failed": {
          target: "recoveryRequired",
          actions: {
            type: "markFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    recoveryRequired: {
      on: {
        "fulfillment.retry": {
          target: "fulfilling",
          actions: assign({ error: null }),
        },
        "submission.succeeded": {
          target: "succeeded",
          actions: {
            type: "markSucceeded",
            params: ({ event }) => ({ result: event.result }),
          },
        },
        "submission.failed": {
          actions: {
            type: "markFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    succeeded: {},
  },
});

export type SubmissionActorRef = ActorRefFrom<typeof submissionMachine>;
export type SubmissionSnapshot = SnapshotFrom<typeof submissionMachine>;

/**
 * Checkout-specific facade over the generic Lit actor bridge. Every method
 * sends a named domain event; there is no arbitrary key/value mutation API.
 */
export class CheckoutSubmissionController {
  private readonly controller: LitActorController<typeof submissionMachine>;

  constructor(host: LitActorHost, kind: CheckoutKind) {
    this.controller = new LitActorController(host, submissionMachine, {
      input: { kind },
    });
  }

  get actor(): SubmissionActorRef {
    return this.controller.actor;
  }

  get snapshot(): SubmissionSnapshot {
    return this.controller.snapshot;
  }

  get attemptId(): string {
    return this.snapshot.context.attemptId;
  }

  get paymentCommitted(): boolean {
    return this.snapshot.context.paymentCommitted;
  }

  get succeeded(): boolean {
    return this.snapshot.matches("succeeded");
  }

  get recoveryRequired(): boolean {
    return this.snapshot.matches("recoveryRequired");
  }

  get busy(): boolean {
    return (
      this.snapshot.context.childBusy ||
      this.snapshot.matches("submitting") ||
      this.snapshot.matches("fulfilling")
    );
  }

  claim(): boolean {
    const event = { type: "submit.requested" } as const;
    if (!this.snapshot.can(event)) return false;
    this.controller.send(event);
    return true;
  }

  paymentRequired(): void {
    this.controller.send({ type: "payment.required" });
  }

  markPaymentCommitted(): void {
    this.controller.send({ type: "payment.committed" });
  }

  /**
   * Completes a synchronous claim. Failed/rejected attempts return to editing
   * with a fresh idempotency key; committed work stays non-chargeable.
   */
  release(succeeded: boolean, error = ""): void {
    if (
      this.succeeded ||
      this.recoveryRequired ||
      this.snapshot.matches("awaitingPayment")
    ) {
      return;
    }
    if (succeeded) {
      this.markPaymentCommitted();
      return;
    }
    this.fail(error);
  }

  succeed(result?: SubmissionResult): void {
    this.controller.send(
      result
        ? { type: "submission.succeeded", result }
        : { type: "submission.succeeded" },
    );
  }

  fail(error: string, paymentCommitted = false): void {
    this.controller.send({
      type: "submission.failed",
      error,
      paymentCommitted,
    });
  }

  setChildBusy(busy: boolean): void {
    this.controller.send({ type: "child.busy.changed", busy });
  }

  setKind(kind: CheckoutKind): void {
    this.controller.send({ type: "checkout.kind.changed", kind });
  }

  retryFulfillment(): void {
    this.controller.send({ type: "fulfillment.retry" });
  }

  reset(): void {
    this.controller.send({ type: "submission.reset" });
  }
}
